// src/components/documents/RoutePreviewMap.js
import React, { useEffect, useRef, useState } from 'react';

/**
 * Shows the origin/destination pins + route preview for a document.
 * When `editable` is true, the person can click the map to drop or move
 * either pin exactly where they want — this is the fallback for addresses
 * (very common with Philippine subdivision streets) that search can't find
 * because that specific street simply isn't in OpenStreetMap's data yet,
 * even though the surrounding subdivision/barangay is.
 */
export default function RoutePreviewMap({
  origin, destination, routeGeometry, height = 220,
  editable = false, onOriginClick, onDestinationClick,
}) {
  const mapRef = useRef(null);
  const leafRef = useRef(null);
  const layersRef = useRef([]);
  const [mapReady, setMapReady] = useState(false);
  const [activeTarget, setActiveTarget] = useState('origin'); // which pin a click sets, in editable mode

  // Auto-switch to placing the destination once the origin is set, so a
  // typical "set A then B" flow doesn't require manually toggling.
  useEffect(() => {
    if (editable && origin?.lat && !destination?.lat) setActiveTarget('destination');
  }, [editable, origin?.lat, destination?.lat]);

  // Init map once
  useEffect(() => {
    if (!mapRef.current) return;
    let cancelled = false;

    function createMap(L) {
      if (cancelled || leafRef.current || !mapRef.current) return;
      if (mapRef.current._leaflet_id) delete mapRef.current._leaflet_id;
      const map = L.map(mapRef.current, {
        center: [14.5995, 120.9842], zoom: 11, zoomControl: editable, attributionControl: false,
      });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '©OpenStreetMap contributors' }).addTo(map);
      leafRef.current = map;
      map.invalidateSize();
      setMapReady(true);
      setTimeout(() => { if (!cancelled && leafRef.current) map.invalidateSize(); }, 250);
    }

    function init(L) {
      if (cancelled) return;
      requestAnimationFrame(() => {
        if (cancelled) return;
        requestAnimationFrame(() => createMap(L));
      });
    }

    const L = window.L;
    if (L) {
      init(L);
    } else {
      const existing = document.querySelector('script[src*="leaflet.js"]');
      if (existing) {
        const poll = setInterval(() => {
          if (cancelled) { clearInterval(poll); return; }
          if (window.L) { clearInterval(poll); init(window.L); }
        }, 50);
        existing.addEventListener('load', () => { clearInterval(poll); if (!cancelled) init(window.L); });
        setTimeout(() => clearInterval(poll), 5000);
      } else {
        const s = document.createElement('script');
        s.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        s.crossOrigin = 'anonymous';
        s.onload = () => { if (!cancelled) init(window.L); };
        document.head.appendChild(s);
      }
    }

    return () => {
      cancelled = true;
      if (leafRef.current) { leafRef.current.remove(); leafRef.current = null; setMapReady(false); }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Click-to-place handler — attached/re-attached whenever editable mode
  // or which pin is "active" changes.
  useEffect(() => {
    const L = window.L;
    if (!L || !leafRef.current || !editable) return;
    const map = leafRef.current;

    const handler = (e) => {
      const pos = { lat: e.latlng.lat, lng: e.latlng.lng };
      if (activeTarget === 'origin') onOriginClick?.(pos);
      else onDestinationClick?.(pos);
    };
    map.on('click', handler);
    return () => map.off('click', handler);
  }, [mapReady, editable, activeTarget, onOriginClick, onDestinationClick]);

  // Draw pins + route
  useEffect(() => {
    const L = window.L;
    if (!L || !leafRef.current) return;
    const map = leafRef.current;

    layersRef.current.forEach(l => map.removeLayer(l));
    layersRef.current = [];

    const bounds = [];
    const pinIcon = (color, label, draggable) => L.divIcon({
      html: `<div style="width:26px;height:26px;border-radius:50% 50% 50% 0;background:${color};transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;${draggable ? 'cursor:grab;' : ''}">
               <span style="transform:rotate(45deg);font-size:11px;color:#fff;font-weight:800;">${label}</span>
             </div>`,
      className: '', iconSize: [26, 26], iconAnchor: [13, 26],
    });

    if (origin?.lat) {
      const m = L.marker([origin.lat, origin.lng], { icon: pinIcon('#10b981', 'A', editable), draggable: editable }).addTo(map);
      if (editable) m.on('dragend', e => onOriginClick?.({ lat: e.target.getLatLng().lat, lng: e.target.getLatLng().lng }));
      layersRef.current.push(m);
      bounds.push([origin.lat, origin.lng]);
    }
    if (destination?.lat) {
      const m = L.marker([destination.lat, destination.lng], { icon: pinIcon('#f97316', 'B', editable), draggable: editable }).addTo(map);
      if (editable) m.on('dragend', e => onDestinationClick?.({ lat: e.target.getLatLng().lat, lng: e.target.getLatLng().lng }));
      layersRef.current.push(m);
      bounds.push([destination.lat, destination.lng]);
    }
    if (routeGeometry && routeGeometry.length > 1) {
      const line = L.polyline(routeGeometry, { color: '#3b82f6', weight: 4, opacity: 0.8 }).addTo(map);
      layersRef.current.push(line);
    }

    if (bounds.length === 2) {
      map.fitBounds(bounds, { padding: [36, 36] });
    } else if (bounds.length === 1) {
      map.setView(bounds[0], 15);
    }
  }, [mapReady, origin, destination, routeGeometry, editable, onOriginClick, onDestinationClick]);

  return (
    <div>
      {editable && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: 'var(--textFaint)' }}>Click the map to place:</span>
          <button
            type="button"
            onClick={() => setActiveTarget('origin')}
            style={{
              fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, cursor: 'pointer',
              background: activeTarget === 'origin' ? '#10b98122' : 'var(--bgInput)',
              border: `1.5px solid ${activeTarget === 'origin' ? '#10b981' : 'var(--borderInput)'}`,
              color: activeTarget === 'origin' ? '#10b981' : 'var(--textMuted)',
            }}
          >
            🟢 Origin
          </button>
          <button
            type="button"
            onClick={() => setActiveTarget('destination')}
            style={{
              fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, cursor: 'pointer',
              background: activeTarget === 'destination' ? '#f9731622' : 'var(--bgInput)',
              border: `1.5px solid ${activeTarget === 'destination' ? '#f97316' : 'var(--borderInput)'}`,
              color: activeTarget === 'destination' ? '#f97316' : 'var(--textMuted)',
            }}
          >
            🟠 Destination
          </button>
          <span style={{ fontSize: 10, color: 'var(--textFaint)' }}>— or drag an existing pin</span>
        </div>
      )}

      {!editable && !origin?.lat && !destination?.lat ? (
        <div style={{ height, borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bgInput)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--textFaint)', fontSize: 12 }}>
          Select origin and destination to preview the route
        </div>
      ) : (
        <div style={{ height, borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)' }}>
          <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
        </div>
      )}
    </div>
  );
}
